/**
 * Escapes a string for safe use inside an HTML attribute value (double-quoted).
 * Prevents breaking out of the attribute and mitigates injection when the value
 * is used in raw HTML (e.g. dangerouslySetInnerHTML).
 */
const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Converts a React/DOM style object key to a CSS property name.
 * Leaves CSS custom properties (--*) unchanged; maps camelCase to kebab-case;
 * special-cases cssFloat -> float.
 */
const reactStyleKeyToCss = (key: string): string => {
  if (key.startsWith("--")) return key;
  if (key === "cssFloat") return "float";
  return key.replace(/([A-Z])/g, (_, c: string) => `-${c.toLowerCase()}`);
};

const styleObjectToCss = (
  style: Record<string, string | undefined | null>,
): string =>
  Object.entries(style)
    .filter(([, val]) => val !== undefined && val !== null)
    .map(([prop, val]) => `${reactStyleKeyToCss(prop)}: ${val}`)
    .join("; ");

/**
 * Helper function to convert props to HTML attributes string
 */
const propsToAttributes = (props: Record<string, any>): string => {
  return Object.entries(props)
    .map(([key, value]) => {
      if (value === undefined || value === null) return "";
      if (value === "true") return ` ${key}`;
      if (value === "false") return "";
      if (key === "style" && typeof value === "object") {
        const cssString = styleObjectToCss(value);
        return ` ${key}="${escapeHtmlAttribute(cssString)}"`;
      }
      return ` ${key}="${escapeHtmlAttribute(String(value))}"`;
    })
    .join("");
};

export default propsToAttributes;
